import sanitizeHtml from 'sanitize-html';

/** Always strip these — meta refresh, base hijack, scripts, forms, etc. */
export const FORBIDDEN_HTML_TAGS = new Set([
  'meta',
  'base',
  'link',
  'script',
  'noscript',
  'style',
  'object',
  'embed',
  'applet',
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'option',
  'frame',
  'frameset',
  'html',
  'head',
  'body',
  'title',
  'template',
]);

export function isForbiddenHtmlTag(tag: string): boolean {
  return FORBIDDEN_HTML_TAGS.has(String(tag || '').toLowerCase());
}

export function hasDangerousAttribs(attribs?: Record<string, string>): boolean {
  if (!attribs) return false;
  for (const key of Object.keys(attribs)) {
    const lower = key.toLowerCase();
    if (lower.startsWith('on')) return true;
    if (lower === 'http-equiv' || lower === 'httpequiv') return true;
    if (lower === 'action' || lower === 'formaction') return true;
    if (lower === 'xlink:href') return true;
  }
  return false;
}

export function exclusiveDangerousHtmlFilter(frame: {
  tag?: string;
  attribs?: Record<string, string>;
}): boolean {
  const tag = String(frame.tag || '').toLowerCase();
  if (isForbiddenHtmlTag(tag)) return true;
  if (hasDangerousAttribs(frame.attribs)) return true;
  return false;
}

export function sanitizeHref(href: string | undefined): string {
  const raw = String(href || '').trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  if (
    lower.startsWith('javascript:') ||
    lower.startsWith('vbscript:') ||
    lower.startsWith('data:') ||
    lower.startsWith('blob:') ||
    // Protocol-relative //evil.com — treat as unsafe unless same-site path
    lower.startsWith('//')
  ) {
    return '';
  }
  // Relative site paths are fine
  if (raw.startsWith('/') && !raw.startsWith('//')) {
    return raw;
  }
  try {
    const parsed = new URL(raw);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:' && parsed.protocol !== 'mailto:') {
      return '';
    }
    return raw;
  } catch {
    // Not an absolute URL — allow hash/query-relative only if no scheme
    if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return '';
    return raw;
  }
}

export function sanitizeImageSrc(src: string | undefined): string {
  const raw = sanitizeHref(src);
  if (!raw) return '';
  const lower = raw.toLowerCase().split('?')[0].split('#')[0];
  if (
    lower.endsWith('.svg') ||
    lower.endsWith('.svgz') ||
    lower.includes('image/svg') ||
    lower.startsWith('data:image/svg')
  ) {
    return '';
  }
  return raw;
}

export const HTML_SECURITY_BASE: Pick<
  sanitizeHtml.IOptions,
  'exclusiveFilter' | 'allowVulnerableTags' | 'disallowedTagsMode' | 'allowedSchemes'
> = {
  exclusiveFilter: exclusiveDangerousHtmlFilter,
  allowVulnerableTags: false,
  disallowedTagsMode: 'discard',
  allowedSchemes: ['http', 'https', 'mailto'],
};

export const ARTICLE_IFRAME_HOSTNAMES = [
  'www.youtube-nocookie.com',
  'www.youtube.com',
  'youtube.com',
  'player.vimeo.com',
  'www.dailymotion.com',
  'www.instagram.com',
  'platform.twitter.com',
  'syndication.twitter.com',
] as const;

/** Public article / live-blog HTML — blocks meta refresh & XSS vectors */
export const PUBLIC_ARTICLE_SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
  ...HTML_SECURITY_BASE,
  allowedTags: [
    'p',
    'br',
    'b',
    'strong',
    'i',
    'em',
    'u',
    's',
    'strike',
    'del',
    'a',
    'img',
    'span',
    'blockquote',
    'code',
    'pre',
    'ul',
    'ol',
    'li',
    'hr',
    'div',
    'figure',
    'figcaption',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'iframe',
    'video',
    'source',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'section',
    'aside',
    'picture',
  ],
  allowedAttributes: {
    a: ['href', 'title', 'target', 'rel', 'class', 'data-tag-id', 'data-tag-slug'],
    img: [
      'src',
      'alt',
      'title',
      'loading',
      'width',
      'height',
      'class',
      'srcset',
      'sizes',
    ],
    code: ['class'],
    span: ['class', 'style'],
    div: [
      'class',
      'style',
      'data-lexical-youtube',
      'data-youtube-id',
      'data-lexical-upload-video',
    ],
    figure: ['class'],
    figcaption: ['class'],
    p: ['class'],
    blockquote: ['class'],
    ul: ['class'],
    ol: ['class', 'start'],
    li: ['class', 'value'],
    h1: ['class'],
    h2: ['class'],
    h3: ['class'],
    h4: ['class'],
    h5: ['class'],
    h6: ['class'],
    section: ['class'],
    aside: ['class'],
    picture: ['class'],
    iframe: [
      'src',
      'title',
      'loading',
      'allow',
      'allowfullscreen',
      'referrerpolicy',
      'class',
      'width',
      'height',
      'frameborder',
      'data-lexical-youtube',
    ],
    video: [
      'src',
      'controls',
      'playsinline',
      'preload',
      'poster',
      'width',
      'height',
      'class',
      'data-lexical-upload-video',
    ],
    source: ['src', 'type', 'srcset', 'sizes', 'media'],
    table: ['class'],
    td: ['colspan', 'rowspan', 'class'],
    th: ['colspan', 'rowspan', 'class'],
  },
  allowedStyles: {
    span: {
      color: [/^#(0x)?[0-9a-f]+$/i, /^rgb/, /^rgba/, /^hsl/, /^hsla/, /^[a-z]+$/i],
      'background-color': [
        /^#(0x)?[0-9a-f]+$/i,
        /^rgb/,
        /^rgba/,
        /^hsl/,
        /^hsla/,
        /^[a-z]+$/i,
      ],
    },
    div: {
      'padding-bottom': [/^\d+(\.\d+)?%$/],
      position: [/^relative$/],
      width: [/^100%$/],
      'max-width': [/^\d+(\.\d+)?(px|%)$/],
      'aspect-ratio': [/^\d+(\.\d+)?\s*\/\s*\d+(\.\d+)?$/],
    },
  },
  allowedSchemesByTag: {
    img: ['https', 'http'],
    a: ['http', 'https', 'mailto'],
    video: ['https', 'http'],
    source: ['https', 'http'],
  },
  allowedIframeHostnames: [...ARTICLE_IFRAME_HOSTNAMES],
  transformTags: {
    a: (_tagName, attribs) => ({
      tagName: 'a',
      attribs: {
        href: sanitizeHref(attribs.href),
        title: attribs.title || '',
        target: attribs.target === '_blank' ? '_blank' : attribs.target || '',
        rel:
          attribs.target === '_blank'
            ? 'noopener noreferrer nofollow ugc'
            : attribs.rel || 'nofollow ugc',
        class: attribs.class || '',
        'data-tag-id': attribs['data-tag-id'] || '',
        'data-tag-slug': attribs['data-tag-slug'] || '',
      },
    }),
    img: (_tagName, attribs) => ({
      tagName: 'img',
      attribs: {
        src: sanitizeImageSrc(attribs.src),
        alt: attribs.alt || '',
        title: attribs.title || '',
        loading: attribs.loading || 'lazy',
        width: attribs.width || '',
        height: attribs.height || '',
        class: attribs.class || '',
        srcset: attribs.srcset || '',
        sizes: attribs.sizes || '',
      },
    }),
  },
};

export function sanitizePublicArticleHtml(raw: string): string {
  return sanitizeHtml(String(raw || ''), PUBLIC_ARTICLE_SANITIZE_OPTIONS).trim();
}

/** Defense-in-depth for already-sanitized comment HTML */
export function resanitizeCommentHtml(raw: string): string {
  return sanitizeHtml(String(raw || ''), {
    ...HTML_SECURITY_BASE,
    allowedTags: [
      'p',
      'br',
      'b',
      'strong',
      'i',
      'em',
      'u',
      's',
      'strike',
      'del',
      'span',
    ],
    allowedAttributes: {
      span: ['class'],
    },
    allowedSchemes: [],
    allowedSchemesByTag: {},
    allowedIframeHostnames: [],
    disallowedTagsMode: 'discard',
  });
}
